import React, { useEffect, useState } from "react";
import AxiosService from "../services/APIService";
import { Button, Card, Col, Flex, Form, Input, List, message, Modal, Popover, Row, Spin, Typography } from "antd";
import moment from "moment";
import CryptoJS from "crypto-js";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import CreateNoteModel from "./CreateNoteModel";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";


export const decryptCredentials = (encryptedSalt, hashedPassword) => {
    const commonSalt = process.env.REACT_APP_DECRYPT_PASSWORD;
    if (!encryptedSalt || !hashedPassword) return "";
    try {
        const decryptedSaltBytes = CryptoJS.AES.decrypt(encryptedSalt, commonSalt);
        const decryptedSalt = decryptedSaltBytes.toString(CryptoJS.enc.Utf8);
        if (!decryptedSalt) return "";
        const decryptedPasswordBytes = CryptoJS.AES.decrypt(hashedPassword, decryptedSalt);
        return decryptedPasswordBytes.toString(CryptoJS.enc.Utf8) || "";
    } catch (error) {
        console.error("Error decrypting credentials:", error);
        return "";
    }
};

const products = ["PG Gold", "PG Silver", "PG Platinum"];

const UserProfile = () => {
    const apiService = new AxiosService();
    const [userData, setUserData] = useState(null);
    const [decryptedPassword, setDecryptedPassword] = useState("");
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
        fetchProfileData();
    }, []);

    const getMissingProductSlots = () => {
        const allSlots = [];
        (userData?.subscription || []).forEach(sub => {
            const planName = sub.planId?.name;
            const count = planName === "PG Platinum" ? 2 : 1;
            const totalForThisSub = count * (sub.planQuantity || 1);
            for (let i = 0; i < totalForThisSub; i++) {
                allSlots.push(planName);
            }
        });

        const usedSlots = (userData?.accountDetails || []).map(acc => acc.productName);
        const missingSlots = [...allSlots];
        usedSlots.forEach(used => {
            const index = missingSlots.indexOf(used);
            if (index !== -1) {
                missingSlots.splice(index, 1);
            }
        });
        return missingSlots;
    };

    const handleSingleSubmit = async (values, product) => {
        try {
            setLoading(true);
            const newAccount = {
                ...values,
                productName: product
            };

            const payload = {
                accountDetails: [...(userData?.accountDetails || []), newAccount]
            };

            await apiService.put(`/auth/update-user/${userData?._id}`, payload);
            message.success(`${product} details updated successfully`);
            fetchProfileData();
            return true;
        } catch (error) {
            message.error(error.message || "Failed to update account details");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fetchProfileData = () => {
        setLoading(true);
        apiService.get("/auth/me").then((res) => {
            setDecryptedPassword(decryptCredentials(res?.user?.salt, res?.user?.hashedPassword));
            setUserData(res.user);
        }).catch((e) => {
            message.error(e.message || "Something went wrong");
        }).finally(() => {
            setLoading(false);
        });
    };

    const renderAccountCard = (account, index) => (
        <Card
            style={{
                height: '100%',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
        >
            <Typography.Title
                level={5}
                style={{
                    textAlign: "center",
                    marginBottom: '20px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #f0f0f0'
                }}
            >
                MT5 Account Detail -{index + 1}   {account.productName || `Account ${index + 1}`}
            </Typography.Title>
            <List
                itemLayout="horizontal"
                split={false}
                style={{ padding: '0 25px' }}
            >
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Broker:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.brokerName}</Typography.Text>
                    </div>
                </List.Item>
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Account Name:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.accountName}</Typography.Text>
                    </div>
                </List.Item>
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Account Number:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.accountNumber}</Typography.Text>
                    </div>
                </List.Item>
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Balance:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.balance}</Typography.Text>
                    </div>
                </List.Item>
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Investor Password:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.investorPassword}</Typography.Text>
                    </div>
                </List.Item>
                <List.Item style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography.Text strong style={{ width: '140px' }}>Broker Server:</Typography.Text>
                        <Typography.Text style={{ marginLeft: '8px' }}>{account.brokerServer}</Typography.Text>
                    </div>
                </List.Item>
            </List>
        </Card>
    );

    const renderAccountInfo = () => {
        if (!userData?.accountDetails?.length) return null;

        return (
            <div style={{ position: "relative", height: '100%' }}>
                {/* Left Arrow */}
                <div className="swiper-button-prev-custom">
                    <LeftOutlined />
                </div>

                {/* Right Arrow */}
                <div className="swiper-button-next-custom">
                    <RightOutlined />
                </div>

                <Swiper
                    modules={[Pagination, Navigation]}
                    loop={userData.accountDetails.length > 1}
                    navigation={{
                        prevEl: ".swiper-button-prev-custom",
                        nextEl: ".swiper-button-next-custom",
                    }}
                    spaceBetween={20}
                    pagination={{
                        el: ".custom-pagination",
                        clickable: true,
                        renderBullet: (index, className) => {
                            return `<span class="${className} custom-bullet"></span>`;
                        },
                    }}
                    className="mySwiper"
                    style={{ height: '100%' }}
                >
                    {userData.accountDetails.map((account, index) => (
                        <SwiperSlide key={index} style={{ height: 'auto' }}>
                            {renderAccountCard(account, index)}
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div className="custom-pagination" />
            </div>
        );
    };


    const getProductDetails = (product) => {
        const commonStyle = {
            title: {
                color: '#000',
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '10px'
            },
            listItem: {
                marginBottom: '8px',
                color: '#333',
                fontSize: '14px'
            },
            highlight: {
                color: '#000',
                fontWeight: 'bold'
            }
        };

        switch (product) {
            case "PG Gold":
                return {
                    title: "PG Gold Details",
                    details: [
                        "MARTINGALE STRATEGY",
                        "Approx. 5-10 % Return",
                        "1 Live Account Licence",
                        "1-Year Validity",
                        "10 Years of Backtesting",
                        "Free Updates For 1 Year",
                        "99% Accuracy Rate",
                        "First Trade Lot Size: 0.01",
                        "Min Account Size: $1000",
                        "1 Strategy, 1 Currency Pair"
                    ],
                    style: commonStyle
                };
            case "PG Silver":
                return {
                    title: "PG Silver Details",
                    details: [
                        "MARTINGALE + HEDGE STRATEGY",
                        "Approx. 5-10 % Return",
                        "1 Live Account Licence",
                        "1-Year Validity",
                        "10 Years of Backtesting",
                        "Free Updates For 1 Year",
                        "99% Accuracy Rate",
                        "First Trade Lot Size: 0.01",
                        "Min Account Size: $1000",
                        "1 Strategy, 1 Currency Pair"
                    ],
                    style: commonStyle
                };
            default:
                return {
                    title: "Product Details",
                    details: [
                        ' MARTINGALE + LOT REPEATER STRATEGY',
                        "Approx. 5-10 % Return",
                        "1 Live Account Licence",
                        "1-Year Validity",
                        "9 Years of Backtesting",
                        "Free Updates For 1 Year",
                        "99% Accuracy Rate",
                        "First Trade Lot Size: 0.01",
                        "Min Account Size: $1000",
                        "1 Strategy, 1 Currency Pair"
                    ],
                    style: commonStyle
                };
        }
    };

    const AccountForm = ({ product, index }) => {
        const [subForm] = Form.useForm();
        const productInfo = getProductDetails(product);

        return (
            <Form
                form={subForm}
                onFinish={async (values) => {
                    const success = await handleSingleSubmit(values, product);
                    if (success) subForm.resetFields();
                }}
                layout="vertical"
            >
                <Card
                    key={index}
                    style={{
                        marginTop: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    styles={{ body: { padding: '12px 24px' } }}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography.Text strong style={{ fontSize: '18px' }}>
                                MT5 Account Details - {index + 1} ({product})
                            </Typography.Text>
                            <Popover
                                content={
                                    <div style={{
                                        maxWidth: '350px',
                                        padding: '10px',
                                        backgroundColor: '#f8f8f8',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }}>
                                        <div style={productInfo.style.title}>{productInfo.title}</div>
                                        <ul style={{
                                            paddingLeft: '20px',
                                            margin: '5px 0',
                                            listStyleType: 'none'
                                        }}>
                                            {productInfo.details.map((detail, i) => (
                                                <li key={i} style={{
                                                    ...productInfo.style.listItem,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    <span style={{
                                                        color: '#000',
                                                        fontSize: '12px'
                                                    }}>•</span>
                                                    {detail}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                }
                                title={null}
                            >
                                <InfoCircleOutlined
                                    style={{
                                        marginLeft: '8px',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        color: '#000'
                                    }}
                                />
                            </Popover>
                        </div>
                    }
                >
                    <Row gutter={[32, 0]}>
                        <Col span={12}>
                            <Form.Item label="Account Name" name="accountName" rules={[{ required: true, message: 'Please enter account name' }]}>
                                <Input size="large" placeholder="Enter Account Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Account Number" name="accountNumber" rules={[{ required: true, message: 'Please enter account number' }]}>
                                <Input size="large" type="Number" placeholder="Enter Account Number" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Balance" name="balance" rules={[{ required: true, message: 'Please enter balance' }]}>
                                <Input size="large" type="Number" placeholder="Enter Balance" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Broker Name" name="brokerName" rules={[{ required: true, message: 'Please enter broker name' }]}>
                                <Input size="large" placeholder="Enter Broker Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Investor Password" name="investorPassword" rules={[{ required: true, message: 'Please enter investor password' }]}>
                                <Input.Password size="large" placeholder="Enter Investor Password" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Broker Server" name="brokerServer" rules={[{ required: true, message: 'Please enter broker server' }]}>
                                <Input size="large" placeholder="Enter Broker Server" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ marginTop: '0px', marginBottom: '12px', textAlign: 'right' }}>
                        <Button type="primary" htmlType="submit" loading={loading} size="large" style={{ padding: '0 40px' }}>
                            Submit {product} Details
                        </Button>
                    </div>
                </Card>
            </Form>
        );
    };

    const renderMt5AccountDetails = () => {
        const missingSlots = getMissingProductSlots();
        if (missingSlots.length === 0) return null;

        return (
            <div style={{ marginTop: '8px' }}>
                {missingSlots.map((product, index) => (
                    <AccountForm key={index} product={product} index={index} />
                ))}
            </div>
        );
    };


    return (
        <div style={{ padding: "24px" }}>
            <Spin spinning={loading} size="large">
                <Row gutter={[24, 24]} style={{ display: 'flex', alignItems: 'stretch' }}>
                    <Col span={12}>
                        <Card
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                height: '100%'
                            }}
                        >
                            <Typography.Title
                                level={5}
                                style={{
                                    marginBottom: '20px',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                            >
                                User Info
                            </Typography.Title>
                            <List
                                itemLayout="horizontal"
                                split={false}
                                style={{ padding: '0 16px' }}
                            >
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Name: </Typography.Text>
                                        {userData?.fullName}
                                    </Typography.Paragraph>
                                </List.Item>
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Email: </Typography.Text>
                                        {userData?.email}
                                    </Typography.Paragraph>
                                </List.Item>
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Phone Number: </Typography.Text>
                                        {userData?.phoneNumber}
                                    </Typography.Paragraph>
                                </List.Item>
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Date of Birth: </Typography.Text>
                                        {moment(userData?.dob).format("DD-MM-YYYY")}
                                    </Typography.Paragraph>
                                </List.Item>
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Password: </Typography.Text>
                                        {decryptedPassword}
                                    </Typography.Paragraph>
                                </List.Item>
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                        <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>Country: </Typography.Text>
                                        {userData?.country}
                                    </Typography.Paragraph>
                                </List.Item>
                                {userData?.userCode && (
                                    <List.Item style={{ padding: '8px 0' }}>
                                        <Typography.Paragraph className="list-text" style={{ margin: 0 }}>
                                            <Typography.Text strong style={{ width: '140px', display: 'inline-block' }}>User Code: </Typography.Text>
                                            {userData?.userCode}
                                        </Typography.Paragraph>
                                    </List.Item>
                                )}
                            </List>
                        </Card>
                    </Col>
                    <Col span={12}>
                        {renderAccountInfo()}
                    </Col>
                </Row>


                <Card
                    style={{
                        marginTop: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    <Flex align="center" justify="space-between" style={{ padding: '16px' }}>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            If you want to change anything in details, please send a note from here.
                        </Typography.Title>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => setIsModalOpen(true)}
                            style={{
                                height: '40px',
                                padding: '0 24px'
                            }}
                        >
                            Create Note
                        </Button>
                    </Flex>
                </Card>

                {renderMt5AccountDetails()}

                <Card
                    style={{
                        marginTop: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    <Typography.Title
                        level={4}
                        style={{
                            marginBottom: '16px',
                            paddingBottom: '10px',
                            borderBottom: '1px solid #f0f0f0'
                        }}
                    >
                        DISCLAIMER
                    </Typography.Title>
                    <Typography.Text style={{ fontSize: 15, lineHeight: '1.6' }}>
                        Pip Grid is an advanced automated trading solution designed to enhance trading efficiency, but it is essential to understand the risks associated with Forex trading. The Forex market involves substantial risk, and even experienced traders may face significant losses. Studies indicate that 85%–95% of manual traders lose their entire capital. Whether trading manually or using an automated system, a thorough understanding of market risks is crucial.
                        <br /><br />
                        While Pip Grid is built to optimize trading strategies, it does not eliminate the inherent risks of Forex trading. Market volatility, execution delays, liquidity fluctuations, and technical failures can lead to unexpected losses. Although our system employs risk management strategies, there are no guarantees of consistent profits, and traders must acknowledge the possibility of losing their entire investment.
                        <br /><br />
                        By using Pip Grid, you accept that trades are executed automatically based on pre-set parameters, which may become less effective due to unpredictable market changes. Risk management settings, including stop-loss limits, must be configured according to your financial goals and risk tolerance. The platform does not provide personalized financial advice, and all trading decisions are made at your own discretion and risk. Additionally, market disruptions, broker-related issues, or technical failures may impact the bot's performance, and the platform is not liable for any resulting financial losses.
                        <br /><br />
                        Forex trading may not be suitable for all investors. Only trade with funds you can afford to lose, and seek independent financial advice if you are uncertain about engaging in automated trading. Past performance does not guarantee future results. By using Pip Grid, you fully accept the risks and take full responsibility for your trading outcomes.
                        <br /><br />
                        PAST PERFORMANCE IS NO GUARANTEE OF FUTURE RESULTS.
                    </Typography.Text>
                </Card>
            </Spin>
            <CreateNoteModel isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} setLoading={setLoading} />
        </div>
    );
};

export default UserProfile;
